/**
 * Auth-Hook für Komponenten.
 * Trennt Login-Mutation (TanStack Query) vom State-Store sauber.
 */

import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/auth';
import { api } from '@/lib/api';
import type { LoginRequest, LoginResponse } from '@/types/api';

export function useAuth(): {
  isAuthenticated: boolean;
  token: string | null;
  expiresAt: number | null;
  logout: () => void;
} {
  const { isAuthenticated, token, expiresAt, clear } = useAuthStore();
  return { isAuthenticated, token, expiresAt, logout: clear };
}

export function useLoginMutation(): ReturnType<
  typeof useMutation<LoginResponse, Error, LoginRequest>
> {
  const setToken = useAuthStore((s) => s.setToken);
  return useMutation<LoginResponse, Error, LoginRequest>({
    mutationFn: (body) =>
      api.post<LoginResponse, LoginRequest>('/auth/login', body, { anonymous: true }),
    onSuccess: (data) => {
      setToken(data.access_token, data.expires_in);
    },
  });
}

export function useLogoutMutation(): ReturnType<typeof useMutation<void, Error, void>> {
  const clear = useAuthStore((s) => s.clear);
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      try {
        await api.post<void>('/auth/logout');
      } catch {
        // Token war evtl. schon ungültig – egal, lokal aufräumen.
      }
    },
    onSettled: () => clear(),
  });
}
